import React, { useEffect, useState, useRef } from 'react';
import styles from './ServicesFrontPage.module.scss';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
// Client-only: EmployeeSlider already returns null until mount, and Swiper CSS
// crashes Next's API webpack runtime (`__webpack_modules__[moduleId] is not a function`)
// when this module is pulled in via faust.config → templates → front-page.
import dynamic from 'next/dynamic';

const EmployeeSlider = dynamic(() => import('../../EmployeeSlider/EmployeeSlider'), {
  ssr: false,
  loading: () => null,
});
import { useQuery, gql } from '@apollo/client';
import ContactCTAAlternative from '../../Global/ContactCTA/ContactCTAAlternative';

import { getLanguageFromPath } from '../../../lib/polylang';
import {
  filterMainNavServices,
  TJENESTER_NAV_NODE_FIELDS,
} from '../../../lib/main-service-filter';
import {
  TJENESTER_WHERE_INLINE_EN,
  TJENESTER_WHERE_INLINE_NB,
} from '../../../lib/tjenester-archive-query';

const TJENESTER_FIELDS_WITH_CHILDREN = `
  nodes {
    ${TJENESTER_NAV_NODE_FIELDS}
    children(first: 20) {
      nodes {
        ... on Tjeneste { id title slug uri }
      }
    }
  }
`;

const GET_SERVICES_DATA = gql`
  query GetServicesData {
    tjenester(first: 100, where: { ${TJENESTER_WHERE_INLINE_NB} }) {
      ${TJENESTER_FIELDS_WITH_CHILDREN}
    }
  }
`;

const GET_SERVICES_DATA_EN = gql`
  query GetServicesDataEn {
    tjenester(first: 100, where: { ${TJENESTER_WHERE_INLINE_EN} }) {
      ${TJENESTER_FIELDS_WITH_CHILDREN}
    }
  }
`;

const ServicesFrontPage = ({ frontPageData, employees, employeesError, sharedData, sectionRef, ui }) => {
  const router = useRouter();
  const { bilde, knapp } = frontPageData || {};
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [activeServiceIndex, setActiveServiceIndex] = useState(0);
  
  // Swipe state for mobile and desktop
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragOffset, setDragOffset] = useState(0); // Real-time drag offset for smooth movement
  
  // Refs for tab buttons
  const tabRefs = useRef([]);
  const hasMountedRef = useRef(false);
  const animationFrameRef = useRef(null);
  const hasDraggedRef = useRef(false); // Track if a drag occurred to prevent click

  const langCode = getLanguageFromPath(router?.asPath || '/').code;

  // Fetch services data with skip condition
  const shouldSkipServicesQuery = !!(sharedData?.tjenester?.nodes);
  
  const { data: servicesData } = useQuery(
    langCode === 'en' ? GET_SERVICES_DATA_EN : GET_SERVICES_DATA,
    { skip: shouldSkipServicesQuery, fetchPolicy: 'cache-first' }
  );
  const tjenester = sharedData?.tjenester?.nodes || servicesData?.tjenester?.nodes || [];
  
  const mainServices = filterMainNavServices(tjenester);

  // Define the desired order of services by keywords to match (Norwegian and English)
  const serviceOrderMap = [
    { keywords: ['utvikling', 'design', 'development'], order: 1 },
    { keywords: ['markedsføring', 'marketing'], order: 2 },
    { keywords: ['prosjekt', 'project'], order: 3 },
    { keywords: ['innhold', 'video', 'content'], order: 4 },
    { keywords: ['ai', 'automatisering', 'automation'], order: 5 },
  ];

  // Function to get service order based on title
  const getServiceOrder = (title) => {
    const lowerTitle = title?.toLowerCase() || '';
    
    for (const { keywords, order } of serviceOrderMap) {
      if (keywords.some(keyword => lowerTitle.includes(keyword))) {
        return order;
      }
    }
    
    return 999; // Put unknown services at the end
  };

  // Sort the services based on the defined order
  const sortedServices = [...mainServices].sort((a, b) => {
    return getServiceOrder(a.title) - getServiceOrder(b.title);
  });

  // Function to get placeholder image based on service title
  // Using cohesive modern workspace/tech images with consistent style
  const getPlaceholderImage = (title) => {
    const lowerTitle = title?.toLowerCase() || '';
    
    if (lowerTitle.includes('utvikling') || lowerTitle.includes('design')) {
      return 'https://images.unsplash.com/photo-1547658719-da2b51169166?w=800&q=80'; // Laptop with code
    } else if (lowerTitle.includes('ai') || lowerTitle.includes('automatisering')) {
      return 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=800&q=80'; // AI/tech concept
    } else if (lowerTitle.includes('markedsføring') || lowerTitle.includes('marketing')) {
      return 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80'; // Analytics dashboard
    } else if (lowerTitle.includes('innhold') || lowerTitle.includes('video')) {
      return 'https://images.unsplash.com/photo-1492619375914-88005aa9e8fb?w=800&q=80'; // Video/content production
    } else if (lowerTitle.includes('prosjekt')) {
      return 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80'; // Project planning
    }
    
    // Default fallback
    return 'https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=800&q=80'; // Modern tech
  };

  // Log any employee data loading errors
  if (employeesError) {
    console.error('Error loading employee data:', employeesError);
  }

  useEffect(() => {
    setMounted(true);
    
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mark component as mounted after first render to prevent scroll on initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      hasMountedRef.current = true;
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Keep active tab centered on mobile only, but don't force scroll at the edges
  useEffect(() => {
    // ONLY apply on mobile (768px and below)
    if (typeof window === 'undefined' || window.innerWidth > 768) return;
    
    // Don't scroll on initial mount - only on user interaction
    if (!hasMountedRef.current) {
      return;
    }
    
    const timer = setTimeout(() => {
      const activeTab = tabRefs.current[activeServiceIndex];
      if (!activeTab) return;
      
      const tabContainer = activeTab.parentElement;
      if (!tabContainer) return;
      
      // Check if we're at first or last tab
      const isFirstTab = activeServiceIndex === 0;
      const isLastTab = activeServiceIndex === sortedServices.length - 1;
      
      // For first and last tabs, use 'start' and 'end' alignment
      // This prevents forcing the container to scroll beyond its natural bounds
      let inlinePosition = 'center';
      if (isFirstTab) {
        inlinePosition = 'start';
      } else if (isLastTab) {
        inlinePosition = 'end';
      }
      
      activeTab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: inlinePosition
      });
    }, 50);
    
    return () => clearTimeout(timer);
  }, [activeServiceIndex, sortedServices.length]);

  // Swipe/Drag handlers for mobile and desktop - optimized for smooth dragging
  const minSwipeDistance = 50; // Minimum distance for a swipe/drag
  const dragThreshold = 10; // Pixels before drag is detected
  
  // Smooth animation helper using requestAnimationFrame
  const updateDragOffset = (offset) => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(() => {
      setDragOffset(offset);
    });
  };
  
  // Touch handlers (mobile) - optimized for smooth dragging
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(false);
    setDragOffset(0);
    hasDraggedRef.current = false; // Reset drag flag
  };
  
  const onTouchMove = (e) => {
    if (touchStart === null) return;
    
    const currentX = e.targetTouches[0].clientX;
    const diff = currentX - touchStart;
    
    setTouchEnd(currentX);
    
    if (Math.abs(diff) > dragThreshold) {
      setIsDragging(true);
      hasDraggedRef.current = true; // Mark that a drag occurred
      // Calculate drag offset as percentage for smooth real-time movement
      const containerWidth = e.currentTarget.offsetWidth;
      const offsetPercent = (diff / containerWidth) * 100;
      updateDragOffset(offsetPercent);
    }
  };
  
  const onTouchEnd = () => {
    if (!touchStart || touchEnd === null) {
      setTouchStart(null);
      setTouchEnd(null);
      setDragOffset(0);
      hasDraggedRef.current = false;
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    const didDrag = hasDraggedRef.current;
    
    // Enable transitions first (before changing index)
    // This ensures smooth animation when position changes
    setIsDragging(false);
    
    // Use requestAnimationFrame to ensure state updates happen in the right order
    requestAnimationFrame(() => {
      // Change index - this will trigger position recalculation
      if (isLeftSwipe) {
        setActiveServiceIndex((prev) => (prev + 1) % sortedServices.length);
      } else if (isRightSwipe) {
        setActiveServiceIndex((prev) => (prev - 1 + sortedServices.length) % sortedServices.length);
      }
      
      // Reset drag offset immediately after index change
      // The transition will smoothly animate from current position (with offset) to final position (without offset)
      requestAnimationFrame(() => {
        setDragOffset(0);
      });
    });
    
    // Prevent clicks for a short period after drag ends
    if (didDrag) {
      setTimeout(() => {
        hasDraggedRef.current = false;
      }, 300); // Prevent clicks for 300ms after drag
    } else {
      hasDraggedRef.current = false;
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };
  
  // Mouse drag handlers (desktop) - optimized for smooth dragging
  const onMouseDown = (e) => {
    // Don't start drag if clicking on a link or button
    if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON' || e.target.closest('a') || e.target.closest('button')) {
      return;
    }
    setDragStart(e.clientX);
    setIsDragging(false);
    setDragOffset(0);
    hasDraggedRef.current = false; // Reset drag flag
  };
  
  const onMouseMove = (e) => {
    if (dragStart === null) return;
    
    const diff = e.clientX - dragStart;
    const distance = Math.abs(diff);
    
    if (distance > dragThreshold) {
      setIsDragging(true);
      hasDraggedRef.current = true; // Mark that a drag occurred
      // Calculate drag offset as percentage for smooth real-time movement
      const containerWidth = e.currentTarget.offsetWidth;
      const offsetPercent = (diff / containerWidth) * 100;
      updateDragOffset(offsetPercent);
    }
  };
  
  const onMouseUp = (e) => {
    if (dragStart === null) return;
    
    const distance = dragStart - e.clientX;
    const isLeftDrag = distance > minSwipeDistance;
    const isRightDrag = distance < -minSwipeDistance;
    const didDrag = hasDraggedRef.current;
    
    if (isDragging && (isLeftDrag || isRightDrag)) {
      // Prevent click event from firing
      e.preventDefault();
      e.stopPropagation();
      
      // Enable transitions first (before changing index)
      // This ensures smooth animation when position changes
      setIsDragging(false);
      
      // Use requestAnimationFrame to ensure state updates happen in the right order
      requestAnimationFrame(() => {
        // Change index - this will trigger position recalculation
        if (isLeftDrag) {
          setActiveServiceIndex((prev) => (prev + 1) % sortedServices.length);
        } else if (isRightDrag) {
          setActiveServiceIndex((prev) => (prev - 1 + sortedServices.length) % sortedServices.length);
        }
        
        // Reset drag offset immediately after index change
        // The transition will smoothly animate from current position (with offset) to final position (without offset)
        requestAnimationFrame(() => {
          setDragOffset(0);
        });
      });
    } else {
      // No slide change, just reset everything immediately
      setIsDragging(false);
      setDragOffset(0);
    }
    
    // Prevent clicks for a short period after drag ends
    if (didDrag) {
      setTimeout(() => {
        hasDraggedRef.current = false;
      }, 300); // Prevent clicks for 300ms after drag
    } else {
      hasDraggedRef.current = false;
    }
    
    setDragStart(null);
  };
  
  const onMouseLeave = () => {
    setDragStart(null);
    setDragOffset(0);
    setTimeout(() => {
      setIsDragging(false);
    }, 100);
  };
  
  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Helper function to format URLs
  const formatUrl = (url) => {
    if (!url) return '#';
    return url.startsWith('http') ? url : `${process.env.NEXT_PUBLIC_SITE_URL || ''}${url}`;
  };

  return (
    <section ref={!isMobile ? sectionRef : null} className={styles.servicesFrontPage}>
      {/* Remove the background video that's overriding the main dark mode background */}
      
      <div className={styles.container}>
        {/* <span className={styles.servicesSmallHeading}>Tjenester</span> */}
        <div
          className={styles.partnerTagline}
          dangerouslySetInnerHTML={{
            __html:
              frontPageData?.lightmode?.topHeroSubheader ||
              ui?.partnerTaglineFallback ||
              '<p>Vi er ikke din leverandør, vi er din partner &amp; kollega</p>'
          }}
          suppressHydrationWarning
        />
        <div
          className={styles.header}
          dangerouslySetInnerHTML={{ __html: frontPageData?.tjenesteoverskrift || '' }}
          suppressHydrationWarning
        />
        
        <div className={styles.interactiveLayout}>
          {/* Left Column - Service Tabs */}
          <div className={styles.serviceTabs}>
            {sortedServices.map((service, index) => {
              const isActive = activeServiceIndex === index;
              
              return (
                <button
                  key={service.id}
                  ref={(el) => (tabRefs.current[index] = el)}
                  className={`${styles.serviceTab} ${isActive ? styles.activeTab : ''}`}
                  onClick={() => setActiveServiceIndex(index)}
                >
                  <span className={styles.tabTitle}>{service.title}</span>
                </button>
              );
            })}
          </div>
          
          {/* Service Content - 3D Circular Carousel with Looping */}
          <div 
            className={styles.serviceContent}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
          >
            {sortedServices.map((service, index) => {
              const isActive = activeServiceIndex === index;
              const calendlyLink = service.tjenesterPost?.buttonUrl;
              const subServices = service.children?.nodes || [];
              
              // Calculate circular position with looping
              const totalServices = sortedServices.length;
              let position = index - activeServiceIndex;
              
              // Wrap around for circular effect
              if (position < -Math.floor(totalServices / 2)) {
                position += totalServices;
              } else if (position > Math.floor(totalServices / 2)) {
                position -= totalServices;
              }
              
              // Handle card click to navigate to service page (define before use)
              const handleCardClick = (e) => {
                // Don't navigate if it was a drag (check both state and ref)
                if (isDragging || hasDraggedRef.current) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                
                // Don't navigate if clicking on a link or button
                if (e.target.tagName === 'A' || e.target.tagName === 'BUTTON' || 
                    e.target.closest('a') || e.target.closest('button')) {
                  return;
                }
                
                // Navigate with Next router for client-side transitions
                router.push(service.uri);
              };
              
              // Mobile: show all cards with slide animation
              // Desktop: show 1 card on each side with 3D effect
              const isVisible = isMobile ? true : Math.abs(position) <= 1; // All cards visible on mobile for animation
              if (!isVisible) return null;
              
              // Calculate depth and size based on distance from center
              const absPosition = Math.abs(position);
              
              if (isMobile) {
                // Mobile: smooth slide animation with hardware acceleration
                const baseTranslateX = position * 100; // Slide by 100% of card width
                const finalTranslateX = baseTranslateX + (isActive ? dragOffset : 0); // Add drag offset for active card
                const mobileOpacity = isActive ? 1 : 0; // Only active card visible
                const mobileScale = isActive ? 1 : 0.95; // Slight scale for smoothness
                
                return (
                  <div 
                    key={service.id} 
                    className={`${styles.contentPanel} ${isActive ? styles.activePanel : styles.backgroundPanel}`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      zIndex: isActive ? 10 : 5,
                      transform: `translate3d(${finalTranslateX}%, 0, 0) scale(${mobileScale})`,
                      opacity: mobileOpacity,
                      pointerEvents: isActive ? 'auto' : 'none',
                      transition: !isDragging 
                        ? 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                        : 'none' // Disable transition only during active drag for smooth real-time movement
                    }}
                    onClick={handleCardClick}
                  >
                    <div className={styles.contentHeader}>
                      <h3>{service.title}</h3>
                      <Link href={service.uri} className={styles.learnMore}>
                        {ui?.learnMore || 'Les mer'}
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z" />
                        </svg>
                      </Link>
                    </div>
                    
                    {service.tjenesterPost?.serviceDescription && (
                      <p>{service.tjenesterPost.serviceDescription}</p>
                    )}
                    
                    {/* Sub-services as pills - always render to maintain consistent card height */}
                    <div className={styles.subServices}>
                      {subServices.map((subService) => (
            <Link 
                          key={subService.id} 
                          href={subService.uri}
                          className={styles.subServicePill}
                        >
                          {subService.title}
                        </Link>
                      ))}
                    </div>

                    {/* Action button */}
                    {calendlyLink && (
                      <div className={styles.actionButtons}>
                        <a 
                          href={calendlyLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.bookButton}
                        >
                          <div className={styles.contentPart}>
                            <span>{ui?.bookIntroMeeting || 'Book gratis intromøte'}</span>
                          </div>
                          <div className={styles.circlePart}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                              <path d="M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z" />
                            </svg>
                          </div>
                        </a>
                      </div>
                    )}
                  </div>
                );
              }
              
              // Desktop: 3D carousel effect with smooth dragging
              const scale = isActive ? 1 : 0.75;
              const cardOpacity = isActive ? 1 : 0.5;
              const contentBlur = isActive ? 0 : 4;
              const baseOffset = 40;
              const baseTranslateX = position * baseOffset;
              const finalTranslateX = baseTranslateX + (isActive ? dragOffset * 0.4 : 0); // Add drag offset with reduced multiplier for 3D effect
              const translateY = absPosition * 10;
              const rotateY = position * -15;
              
              return (
                <div 
              key={service.id} 
                  className={`${styles.contentPanel} ${isActive ? styles.activePanel : styles.backgroundPanel}`}
                  style={{
                    zIndex: isActive ? 10 : 8 - absPosition,
                    transform: `translate3d(${finalTranslateX}%, ${translateY}px, 0) scale(${scale}) rotateY(${rotateY}deg)`,
                    opacity: cardOpacity,
                    pointerEvents: isActive ? 'auto' : 'none',
                    transition: !isDragging
                      ? 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                      : 'none' // Disable transition only during active drag for smooth real-time movement
                  }}
                  data-blur={contentBlur}
                  onClick={handleCardClick}
                >
                  <div className={styles.contentHeader}>
                <h3>{service.title}</h3>
                    <Link href={service.uri} className={styles.learnMore}>
                      {ui?.learnMore || 'Les mer'}
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z" />
                      </svg>
                    </Link>
                  </div>
                  
                {service.tjenesterPost?.serviceDescription && (
                  <p>{service.tjenesterPost.serviceDescription}</p>
                )}
                  
                  {/* Sub-services as pills - always render to maintain consistent card height */}
                  <div className={styles.subServices}>
                    {subServices.map((subService) => (
                      <Link 
                        key={subService.id} 
                        href={subService.uri}
                        className={styles.subServicePill}
                      >
                        {subService.title}
                      </Link>
                    ))}
                  </div>

                  {/* Action button */}
                  {calendlyLink && (
                    <div className={styles.actionButtons}>
                      <a 
                        href={calendlyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.bookButton}
                      >
                        <div className={styles.contentPart}>
                          <span>{ui?.bookIntroMeeting || 'Book gratis intromøte'}</span>
                        </div>
                        <div className={styles.circlePart}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path d="M16.172 11l-5.364-5.364 1.414-1.414L20 12l-7.778 7.778-1.414-1.414L16.172 13H4v-2z" />
                          </svg>
                        </div>
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Swipe indicators (dots) for mobile */}
          {isMobile && (
            <div className={styles.swipeIndicators}>
              {sortedServices.map((_, index) => (
                <button
                  key={index}
                  className={`${styles.swipeDot} ${activeServiceIndex === index ? styles.activeDot : ''}`}
                  onClick={() => setActiveServiceIndex(index)}
                  aria-label={`Go to service ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* <span className={styles.servicesSmallHeading}>Ansatte</span>
        <h2 className={styles.teamHeading}>Våre spesialister</h2> */}
        
        {bilde?.node && (
          <div className={styles.imageContainer}>
            <Image
              src={bilde.node.mediaItemUrl}
              alt={bilde.node.altText || ''}
              width={1920}
              height={820}
              className={styles.image}
              loading="lazy"
            />
            {knapp && (
              <Link 
                href={formatUrl(knapp.buttonLink?.nodes?.[0]?.link)} 
                className={styles.imageButton}
                aria-label={knapp.buttonText}
              >
                {knapp.buttonText}
                <div className={`${styles.arrow} hover-an-right`}>
                  <Image 
                    src="/right-up.png" 
                    alt="Arrow" 
                    width={30} 
                    height={30} 
                  />
                </div>
              </Link>
            )}
          </div>
        )}
        
        {/* Employee Slider Section - show if we have data and no errors */}
        {employees && employees.nodes && employees.nodes.length > 0 && !employeesError && (
          <div className={styles.employeeSection}>
            <EmployeeSlider 
              employees={employees} 
              title="" 
              key="employee-slider"
            />
          </div>
        )}

        
      </div>
    </section>
  );
};

export default ServicesFrontPage; 